import React, { forwardRef, useRef, useState, useEffect, type ChangeEvent } from 'react';
import { ChevronDown } from '@core/icons-react';
import { LoadingIndicator } from '../../indicators';
import { InputWrapper } from '../shared/InputWrapper/InputWrapper';
import { useNormalizedInputProps } from '../shared/hooks/useNormalizedInputProps';
import type { BaseInputProps } from '../shared/types/input.types';
import { OptionDropdown, DropdownOption as OptionDropdownOption } from '../../dropdown/OptionDropdown';
import { useModalInputLayer, useModalDropdownVariant } from '../../modal/ModalContext';

interface DropdownInputOption extends OptionDropdownOption {
  value?: any;
  description?: string;
  metadata?: any;
}

// Re-export for external use
export type DropdownOption = DropdownInputOption;

export interface DropdownInputProps extends Omit<BaseInputProps<HTMLButtonElement>, 'onChange' | 'value'>, 
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseInputProps<HTMLButtonElement> | 'onChange' | 'value' | 'type'> {
  /**
   * The options to display in the dropdown
   */
  options: DropdownInputOption[];
  
  /**
   * The selected value(s) - string for single select, string[] for multiSelect
   */
  value?: string | string[];
  
  /**
   * Callback when selection changes
   */
  onChange?: (value: string | string[]) => void;
  
  /**
   * Enable multi-selection mode
   */
  multiSelect?: boolean;
  
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  
  /**
   * Specify which field layer to use for the background (1, 2, or 3)
   * field-01: Base layer (default)
   * field-02: Darker in light mode, lighter in dark mode
   * field-03: Maximum contrast from base
   */
  layer?: 1 | 2 | 3;
  
  /**
   * Show search input in dropdown
   */
  showSearch?: boolean;
  
  /**
   * Placeholder for the search input when showSearch is true
   */
  searchPlaceholder?: string;
  
  /**
   * Custom render function for displaying selected value
   */
  renderValue?: (value: string | string[], options: DropdownInputOption[]) => React.ReactNode;
  
  /**
   * Show checkmarks for selected items
   */
  showCheckmarks?: boolean;
  
  /**
   * Maximum height of the dropdown
   */
  maxDropdownHeight?: string;
  
  /**
   * Dropdown position relative to the input
   */
  dropdownPosition?: 'left' | 'right';
  
  /**
   * Dropdown direction
   */
  dropdownDirection?: 'down' | 'up';
  
  /**
   * Whether the dropdown is in a loading state
   */
  loading?: boolean;
  
  /**
   * Variant of the dropdown - affects the visual style
   */
  variant?: 'default' | 'strong' | 'subtle';
  
  /**
   * Message to display when there are no options
   */
  emptyMessage?: string;

  /**
   * Enable server-side search: emits the typed term (debounce upstream) instead
   * of filtering `options` locally. Implies a visible search box.
   */
  onSearch?: (term: string) => void;
}

export const DropdownInput = forwardRef<HTMLButtonElement, DropdownInputProps>(function DropdownInput(
  {
    className,
    disabled = false,
    enableCounter = false,
    helperText,
    hideLabel,
    id,
    inline = false,
    invalid = false,
    invalidText,
    labelText,
    layer,
    maxCount,
    options = [],
    value,
    onChange = () => {},
    multiSelect = false,
    placeholder = 'Select an option',
    readOnly,
    required,
    size = 'md',
    warn = false,
    warnText,
    showSearch = false,
    searchPlaceholder = 'Search...',
    renderValue,
    showCheckmarks = true,
    maxDropdownHeight = '320px',
    dropdownPosition = 'left',
    dropdownDirection = 'down',
    loading = false,
    variant,
    emptyMessage,
    onSearch,
    ...rest
  },
  ref
) {
  const prefix = 'core';
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mergedRef = ref || buttonRef;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get effective layer and variant from modal context or user props
  const effectiveLayer = useModalInputLayer(layer, 1);
  const effectiveVariant = useModalDropdownVariant(variant, 'default');
  
  const normalizedProps = useNormalizedInputProps({
    id,
    readOnly,
    disabled,
    invalid,
    invalidText,
    warn,
    warnText,
  });
  
  // Get display text for selected value(s)
  const getDisplayText = () => {
    if (renderValue) {
      const rendered = renderValue(value || (multiSelect ? [] : ''), options);
      // If renderValue returns undefined or null, show placeholder
      if (rendered === undefined || rendered === null) {
        return placeholder;
      }
      return rendered;
    }
    
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return placeholder;
    }
    
    if (multiSelect && Array.isArray(value)) {
      const selectedOptions = options.filter(opt => value.includes(opt.id));
      if (selectedOptions.length === 0) return placeholder;
      if (selectedOptions.length === 1) return selectedOptions[0].label;
      return `${selectedOptions.length} selected`;
    }
    
    const selectedOption = options.find(opt => opt.id === value);
    return selectedOption ? selectedOption.label : placeholder;
  };
  
  const dropdownInputClasses = [
    `${prefix}-dropdown-input`,
    normalizedProps.invalid && `${prefix}-dropdown-input--invalid`,
    normalizedProps.warn && `${prefix}-dropdown-input--warning`,
    size && `${prefix}-dropdown-input--${size}`,
    effectiveLayer && effectiveLayer !== 1 && `${prefix}-dropdown-input--field-${effectiveLayer}`,
    isOpen && `${prefix}-dropdown-input--open`,
    !value && `${prefix}-dropdown-input--placeholder`,
    className,
  ].filter(Boolean).join(' ');
  
  const handleToggle = () => {
    if (!normalizedProps.disabled && !readOnly) {
      setIsOpen(!isOpen);
    }
  };
  
  const handleSelect = (id: string) => {
    if (!multiSelect) {
      onChange(id);
      setIsOpen(false);
    }
  };
  
  const handleMultiSelect = (ids: string[]) => {
    if (multiSelect) {
      onChange(ids);
    }
  };
  
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };
  
  const button = (
    <button
      {...rest}
      ref={mergedRef as React.Ref<HTMLButtonElement>}
      id={id}
      type="button"
      className={dropdownInputClasses}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      disabled={normalizedProps.disabled}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-invalid={normalizedProps.invalid || undefined}
      aria-describedby={helperText ? normalizedProps.helperId : undefined}
      data-invalid={normalizedProps.invalid || undefined}
    >
      <span className={`${prefix}-dropdown-input__text`}>
        {getDisplayText()}
      </span>
      {loading ? (
        <LoadingIndicator 
          className={`${prefix}-dropdown-input__loading-spinner`}
          size="medium"
          color="default"
        />
      ) : (
        <ChevronDown 
          className={`${prefix}-dropdown-input__icon`} 
          size={16}
        />
      )}
    </button>
  );
  
  return (
    <InputWrapper
      className={className}
      disabled={normalizedProps.disabled}
      enableCounter={false}
      helperText={helperText}
      hideLabel={hideLabel}
      id={id}
      inline={inline}
      invalid={normalizedProps.invalid}
      invalidText={invalidText}
      labelText={labelText}
      readOnly={readOnly}
      required={required}
      size={size}
      warn={normalizedProps.warn}
      warnText={warnText}
    >
      {button}
      <OptionDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        options={options}
        selectedItem={!multiSelect ? (value as string) : undefined}
        selectedItems={multiSelect ? (value as string[]) : undefined}
        onSelectionChange={handleSelect}
        onMultiSelectionChange={handleMultiSelect}
        multiSelect={multiSelect}
        renderFooter={true}
        position={dropdownPosition}
        direction={dropdownDirection}
        showCheckmarks={showCheckmarks}
        maxHeight={maxDropdownHeight}
        anchorRef={buttonRef}
        size="medium"
        variant={variant ? effectiveVariant : (effectiveVariant === 'strong' ? 'strong' : (effectiveLayer >= 2 ? 'subtle' : 'default'))}
        minWidth={buttonRef.current ? `${buttonRef.current.offsetWidth}px` : '200px'}
        emptyMessage={emptyMessage}
        searchable={showSearch || !!onSearch}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={onSearch}
      />
    </InputWrapper>
  );
});
